import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Clock } from "lucide-react";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import { Kpi, PageHeader, BillingBadge, MerchantBadge, Money, StatusBadge, Empty } from "@/components/Primitives";
import { chargeTarget } from "@/lib/constants";

function age(iso) {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${Math.floor(m / 1440)}d`;
}

export default function PaymentDesk() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState(null);
  const [sum, setSum] = useState(null);
  const load = useCallback(() => {
    const status = tab === "pending" ? "pending" : "charged,partial,declined,failed,voided";
    api.get("/payments/queue", { params: { status } }).then((r) => setRows(r.data));
    api.get("/payments/summary").then((r) => setSum(r.data));
  }, [tab]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  return (
    <div data-testid="payment-desk-page">
      <PageHeader eyebrow="Billing operations" title="Payment Desk" />
      {sum && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Kpi testId="kpi-pending-count" label="Pending charges" value={sum.pending_count} accent="text-amber-400" />
          <Kpi testId="kpi-pending-amount" label="Pending amount" value={fmtMoney(sum.pending_amount)} accent="font-mono" />
          <Kpi testId="kpi-processed-today" label="Processed today" value={sum.processed_today} />
          <Kpi testId="kpi-charged-today" label="Charged today" value={fmtMoney(sum.charged_today)} accent="font-mono text-emerald-400" />
        </div>
      )}
      <div className="flex gap-1 mb-4">
        {[["pending", "Pending queue"], ["history", "Processed history"]].map(([k, l]) => (
          <button key={k} data-testid={`desk-tab-${k}`} onClick={() => { setTab(k); setRows(null); }} className={`px-4 h-9 text-sm rounded-md border transition-colors ${tab === k ? "bg-indigo-600 border-indigo-500 text-white" : "border-slate-700 text-slate-400 hover:text-slate-100"}`}>{l}</button>
        ))}
      </div>
      {rows === null ? <div className="text-slate-500">Loading…</div> : !rows.length ? <Empty text={tab === "pending" ? "Queue is clear. Nothing to charge." : "No processed records yet."} testId="desk-empty" /> : (
        <div className="panel overflow-x-auto" data-testid="desk-table">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
              <tr><th className="px-4 py-3">Booking</th><th className="px-4 py-3">Agent</th><th className="px-4 py-3">Traveler / Product</th><th className="px-4 py-3">Billing</th><th className="px-4 py-3 text-right">Charge</th><th className="px-4 py-3">Merchant</th><th className="px-4 py-3">Card</th><th className="px-4 py-3">{tab === "pending" ? "Waiting" : "Status"}</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="border-b border-slate-800/70 last:border-0 hover:bg-slate-800/50" data-testid={`desk-row-${b.booking_ref}`}>
                  <td className="px-4 py-3 font-mono font-semibold text-indigo-300">{b.booking_ref}</td>
                  <td className="px-4 py-3">{b.agent_name}</td>
                  <td className="px-4 py-3">{b.traveler_name}<div className="text-xs text-slate-500">{b.product_category} · {b.supplier_name}</div></td>
                  <td className="px-4 py-3"><BillingBadge type={b.billing_type} /></td>
                  <td className="px-4 py-3 text-right"><Money value={tab === "pending" ? chargeTarget(b) : (b.payment?.charged_amount ?? chargeTarget(b))} tone="gross" /></td>
                  <td className="px-4 py-3"><MerchantBadge name={b.payment?.processed_merchant_name || b.target_merchant_name} /></td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{b.card.card_type} ····{b.card.last4}</td>
                  <td className="px-4 py-3">{tab === "pending" ? <span className="inline-flex items-center gap-1 text-xs text-amber-300"><Clock className="h-3 w-3" />{age(b.submitted_at || b.created_at)}</span> : <StatusBadge status={b.charge_status} />}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/bookings/${b.id}`} data-testid={`desk-open-${b.booking_ref}`} className="inline-flex items-center gap-1 text-xs font-semibold px-3 h-8 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white"><CreditCard className="h-3.5 w-3.5" />{tab === "pending" ? "Reveal & process" : "View"}</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows && tab === "history" && <div className="text-xs text-slate-600 mt-2">Last updated {fmtDate(new Date().toISOString())}</div>}
    </div>
  );
}
